import React from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowRight, ArrowLeft, Check, ArrowUpRight } from "lucide-react";
import PageHeader from "@/components/PageHeader";
import CTABanner from "@/components/CTABanner";
import { Reveal, Overline } from "@/components/Reveal";
import { getServiceBySlug, getNeighbourServices, SERVICES } from "@/data/services";

export default function ServiceDetailPage() {
  const { slug } = useParams();
  const service = getServiceBySlug(slug);

  if (!service) return <Navigate to="/services" replace />;

  const { prev, next } = getNeighbourServices(slug);

  return (
    <div data-testid={`page-service-${slug}`}>
      <PageHeader
        title={service.title}
        breadcrumb={[
          { label: "Services", to: "/services" },
          { label: service.title },
        ]}
      />

      {/* Service hero */}
      <section className="relative pt-8 pb-20 sm:pb-28">
        <div className="max-page container-px">
          <div className="grid lg:grid-cols-12 gap-8 items-end mb-12 lg:mb-16">
            <Reveal className="lg:col-span-8">
              <Overline>{`Service ${service.n} / 09`}</Overline>
              <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl xl:text-[5rem] leading-[1.02] tracking-tight text-foreground">
                {service.title}
              </h1>
            </Reveal>
            <Reveal delay={0.1} className="lg:col-span-4">
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {service.short}
              </p>
            </Reveal>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
            <Reveal className="lg:col-span-7">
              <div className="relative overflow-hidden rounded-2xl border border-border">
                <div className="aspect-[4/3]">
                  <img
                    src={service.img}
                    alt={service.title}
                    className="w-full h-full object-cover"
                    data-testid="service-hero-image"
                  />
                </div>
              </div>
            </Reveal>

            <Reveal delay={0.1} className="lg:col-span-5 lg:sticky lg:top-32 self-start">
              <Overline>Overview</Overline>
              <p className="mt-6 text-base sm:text-lg text-foreground leading-relaxed">
                {service.long}
              </p>
              <Link
                to="/contact"
                data-testid="service-cta-quote"
                className="group mt-8 inline-flex items-center gap-3 bg-primary text-primary-foreground px-7 py-3.5 rounded-full text-sm tracking-wide hover:bg-foreground transition-all duration-500"
              >
                Request a Quote
                <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Features list */}
      <section className="section-y bg-muted/40">
        <div className="max-page container-px grid lg:grid-cols-12 gap-10 lg:gap-16">
          <Reveal className="lg:col-span-5">
            <Overline>What we supply</Overline>
            <h2 className="mt-6 font-display text-4xl sm:text-5xl leading-[1.05] text-foreground">
              Curated. Vetted.<br />
              <em className="not-italic text-primary/80">Delivered on time.</em>
            </h2>
            <p className="mt-6 text-muted-foreground leading-relaxed max-w-md">
              Every category is curated — we supply what passes our standards and stand behind every shipment.
            </p>
          </Reveal>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {service.features.map((f, i) => (
              <Reveal key={f} delay={i * 0.05}>
                <div
                  data-testid={`service-feature-${i}`}
                  className="h-full p-7 rounded-2xl border border-border bg-card flex items-start gap-4 hover:-translate-y-1 hover:shadow-[0_18px_50px_-20px_rgba(30,58,138,0.18)] transition-all duration-500"
                >
                  <span className="mt-1 w-9 h-9 rounded-full bg-primary/10 text-primary flex items-center justify-center shrink-0">
                    <Check className="w-4 h-4" strokeWidth={2} />
                  </span>
                  <p className="font-medium text-foreground leading-snug">{f}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Related services — prev/next */}
      <section className="section-y">
        <div className="max-page container-px">
          <Reveal>
            <Overline>Other capabilities</Overline>
            <h2 className="mt-6 font-display text-3xl sm:text-4xl text-foreground">
              Continue exploring our verticals
            </h2>
          </Reveal>

          <div className="mt-12 grid sm:grid-cols-2 gap-6 lg:gap-8">
            <NavCard service={prev} direction="prev" />
            <NavCard service={next} direction="next" />
          </div>

          <Reveal delay={0.1}>
            <Link
              to="/services"
              data-testid="back-to-services"
              className="mt-12 inline-flex items-center gap-2 text-sm tracking-wide text-foreground/70 hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              View all {SERVICES.length} verticals
            </Link>
          </Reveal>
        </div>
      </section>

      <CTABanner />
    </div>
  );
}

const NavCard = ({ service, direction }) => (
  <Reveal>
    <Link
      to={`/services/${service.slug}`}
      data-testid={`related-service-${direction}`}
      className="group relative block rounded-2xl overflow-hidden border border-border bg-card hover:-translate-y-1 hover:shadow-[0_20px_50px_-20px_rgba(30,58,138,0.18)] transition-all duration-500"
    >
      <div className="grid sm:grid-cols-5 items-stretch">
        <div className="sm:col-span-2 relative aspect-[4/3] sm:aspect-auto overflow-hidden">
          <img
            src={service.img}
            alt={service.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
          />
        </div>
        <div className="sm:col-span-3 p-7 flex flex-col justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-3">
              {direction === "prev" ? "Previous" : "Next"} · {service.n} / 09
            </div>
            <h3 className="font-display text-2xl text-foreground leading-tight">
              {service.title}
            </h3>
            <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{service.short}</p>
          </div>
          <div className="mt-6 inline-flex items-center gap-2 text-sm text-primary">
            View capability
            <ArrowUpRight className="w-4 h-4 transition-transform duration-500 group-hover:rotate-45" />
          </div>
        </div>
      </div>
    </Link>
  </Reveal>
);
